import axios from "axios";
import { Howl, Howler } from "howler";
import LogRocket from "logrocket";
import { addBreadcrumb } from "../utils";

let timers = [];
let currentPlaying = null;
let audioCache = null;
let metadataStore = new Map(); // Para guardar isDeletable e outras configs

const CACHE_NAME = "flexge-kids-audios";

// Inicializa o cache storage
const initCache = async () => {
  if (!audioCache) {
    try {
      audioCache = await caches.open(CACHE_NAME);
      console.log("Audio cache inicializado:", CACHE_NAME);
    } catch (error) {
      console.error("Erro ao inicializar cache de áudio:", error);
      LogRocket.log({ error: "cache_init_error", details: error.message });
      throw error;
    }
  }
  return audioCache;
};

const clearTimers = () => {
  timers = timers.filter((id) => {
    clearTimeout(id);
    return false;
  });
};

const downloadAudio = async ({ url, isRetry }) => {
  let audioBlob = null;
  try {
    const response = await axios.get(
      `${isRetry ? process.env.REACT_APP_FILES_FALLBACK_URL : process.env.REACT_APP_FILES_URL}/${url}`,
      { responseType: "blob" }
    );

    audioBlob = response.data;

    // Ajusta o tipo MIME se necessário
    if (audioBlob.type === "application/octet-stream") {
      const extension = url.split(".").pop().toLowerCase();
      const mimeTypes = {
        mp3: "audio/mpeg",
        ogg: "audio/ogg",
        wav: "audio/x-wav",
        webm: "audio/webm",
      };

      const correctMimeType = mimeTypes[extension] || "audio/mpeg";
      audioBlob = new Blob([audioBlob], { type: correctMimeType });
    }
  } catch (error) {
    if (error.response) {
      LogRocket.log({
        error: `${error.response.status}: ${error.response.statusText}`,
        flow: "DOWNLOAD_AUDIO_DATA",
      });
    } else if (error.request) {
      LogRocket.log({ error: `without_response`, flow: "DOWNLOAD_AUDIO_DATA" });
    } else {
      LogRocket.log({ error: error.message, flow: "DOWNLOAD_AUDIO_DATA" });
    }
    return null;
  }

  return audioBlob;
};

// Cria uma chave única para o cache baseada na URL
const getCacheKey = (url) => {
  const baseUrl = process.env.REACT_APP_FILES_URL || process.env.REACT_APP_FILES_FALLBACK_URL;
  return `${baseUrl}/${url}`;
};

export const insertSpeechRecognitionAudio = async ({ key, data, isDeletable }) => {
  try {
    await initCache();

    // Salva no cache storage
    const response = new Response(data, {
      headers: {
        "Content-Type": data.type || "audio/mpeg",
        "Content-Length": data.size,
      },
    });

    await audioCache.put(key, response);

    // Salva metadados
    metadataStore.set(key, { isDeletable, isSpeechRecognition: true });

    return key; // Retorna a chave para uso posterior
  } catch (error) {
    console.error("Erro ao inserir áudio de speech recognition:", error);
    LogRocket.log({ error: "insert_speech_audio_error", details: error.message });
    throw error;
  }
};

export const insertAudio = async ({ url, isDeletable }) => {
  try {
    await initCache();

    const cacheKey = getCacheKey(url);

    // Verifica se já está em cache
    const existingResponse = await audioCache.match(cacheKey);
    if (existingResponse) {
      console.log("Áudio já está em cache:", url);
      metadataStore.set(url, { isDeletable });
      return;
    }

    // Primeira tentativa de download
    const audioBlob = await downloadAudio({ url, isRetry: false });

    if (audioBlob) {
      // Salva no cache storage
      const response = new Response(audioBlob, {
        headers: {
          "Content-Type": audioBlob.type || "audio/mpeg",
          "Content-Length": audioBlob.size,
        },
      });

      await audioCache.put(cacheKey, response);
      console.log("Áudio adicionado ao cache:", url);
    }

    // Salva metadados
    metadataStore.set(url, { isDeletable });

    // Retry com fallback se falhou
    if (!audioBlob) {
      setTimeout(async () => {
        try {
          const retryAudioBlob = await downloadAudio({ url, isRetry: true });

          if (retryAudioBlob) {
            const response = new Response(retryAudioBlob, {
              headers: {
                "Content-Type": retryAudioBlob.type || "audio/mpeg",
                "Content-Length": retryAudioBlob.size,
              },
            });

            await audioCache.put(cacheKey, response);
            console.log("Áudio adicionado ao cache (retry):", url);
          }
        } catch (retryError) {
          console.error("Erro no retry do áudio:", url, retryError);
          LogRocket.log({ error: "audio_retry_error", url, details: retryError.message });
        }
      }, 500);
    }
  } catch (error) {
    console.error("Erro ao inserir áudio:", url, error);
    LogRocket.log({ error: "insert_audio_error", url, details: error.message });
  }
};

export const clearAudios = async () => {
  try {
    await initCache();

    // Encontra URLs que devem ser deletadas
    const urlsToDelete = [];
    for (const [key, metadata] of metadataStore.entries()) {
      if (metadata.isDeletable) {
        urlsToDelete.push(key);
      }
    }

    // Remove do cache storage
    for (const url of urlsToDelete) {
      const metadata = metadataStore.get(url);
      const cacheKey = metadata.isSpeechRecognition ? url : getCacheKey(url);
      await audioCache.delete(cacheKey);
      metadataStore.delete(url);
    }

    console.log(`${urlsToDelete.length} áudios removidos do cache`);
  } catch (error) {
    console.error("Erro ao limpar áudios:", error);
    LogRocket.log({ error: "clear_audios_error", details: error.message });
  }
};

// Modificado: Retorna o Blob diretamente
export const getAudio = async ({ url }) => {
  try {
    await initCache();

    const metadata = metadataStore.get(url);
    if (!metadata) {
      throw new Error("audio_register_not_found");
    }

    const cacheKey = metadata.isSpeechRecognition ? url : getCacheKey(url);
    const cachedResponse = await audioCache.match(cacheKey);

    if (!cachedResponse) {
      throw new Error("audio_data_not_available");
    }

    const blob = await cachedResponse.blob();
    return blob;
  } catch (error) {
    console.error("Erro ao obter áudio:", url, error);
    throw error;
  }
};

export const getAudioInsertConfigs = ({ url }) => {
  const metadata = metadataStore.get(url);
  if (!metadata) {
    throw new Error("audio_register_not_found");
  }
  return { data: null, ...metadata }; // data será obtido via getAudio
};

// Modificado: Usa Blob e createObjectURL com Howler
export const playAudio = async ({ url, rate, resolveOnPlay }) => {
  let audioBlob = null;
  let attempts = 0;
  let error = null;

  while (!audioBlob && attempts < 2) {
    try {
      audioBlob = await getAudio({ url });
    } catch (e) {
      error = e;
      const configs = getAudioInsertConfigs({ url });
      addBreadcrumb({
        category: "audio-store",
        message: `Failed to get audio: ${url}. Trying to recreate it`,
        data: {
          url,
          configs,
        },
      });
      await insertAudio({
        url,
        ...configs,
      });
      attempts++;
    }
  }

  if (!audioBlob) {
    throw error;
  }

  let objectURL = null;
  try {
    objectURL = URL.createObjectURL(audioBlob);

    const sound = await new Promise((resolve, reject) => {
      const sound = new Howl({
        src: [objectURL],
        format: url.startsWith("sr-") ? audioBlob.type.split(";").at(0).split("/").at(1) : url.split(".").pop(),
        autoplay: false,
        loop: false,
        onload: () => resolve(sound),
        onloaderror: (id, error) => {
          addBreadcrumb({
            category: "audio-store",
            action: `error_load_audio: ${url}`,
            data: {
              url,
              error,
              hasAudioData: !!audioBlob,
            },
          });
          reject(`error_load_audio`);
        },
      });
    });

    let playTimeout = null;
    let suspendTimeout = null;
    let onEndTimeout = null;
    currentPlaying = sound;

    await new Promise((resolve, reject) => {
      currentPlaying.once("end", () => {
        clearTimers();
        if (currentPlaying) {
          currentPlaying.unload();
          currentPlaying = null;
        }
        resolve();
      });

      currentPlaying.once("playerror", (id, error) => {
        addBreadcrumb({
          category: "audio-flow",
          action: "Error playing audio",
          data: { url },
        });
        clearTimers();
        if (currentPlaying) {
          currentPlaying.unload();
          currentPlaying = null;
        }
        reject(error);
      });

      currentPlaying.once("play", () => {
        clearTimers();

        let onEndTimeoutMilliseconds = 3000;
        if (currentPlaying && currentPlaying.duration()) {
          onEndTimeoutMilliseconds = (currentPlaying.duration() * 1000) / (rate || 1) + 500;
        }

        onEndTimeout = setTimeout(() => {
          LogRocket.log("audio end timeout. Howler state " + Howler.state);
          clearTimers();
          if (currentPlaying) {
            currentPlaying.unload();
            currentPlaying = null;
          }
          resolve();
        }, onEndTimeoutMilliseconds);
        timers.push(onEndTimeout);

        if (resolveOnPlay) {
          clearTimers();
          resolve(currentPlaying);
        }
      });

      currentPlaying.once("stop", () => {
        clearTimers();
      });

      currentPlaying.rate(rate || 1);

      suspendTimeout = setTimeout(() => {
        if (Howler.state === "suspended") {
          LogRocket.log(Howler.ctx);
          if (currentPlaying) {
            currentPlaying.unload();
            currentPlaying = null;
          }
          reject("required-user-click");
        }
      }, 3000);
      timers.push(suspendTimeout);

      playTimeout = setTimeout(() => {
        if (currentPlaying) {
          LogRocket.log(`Audio state: ${currentPlaying.state()}`);
          currentPlaying.unload();
          currentPlaying = null;
        }
        reject("not-started");
      }, 15000);
      timers.push(playTimeout);

      currentPlaying.play();
    });
  } catch (error) {
    LogRocket.log(error);
    stopAudio();
    throw error;
  } finally {
    clearTimers();
    if (objectURL) {
      URL.revokeObjectURL(objectURL); // Libera memória
    }
  }
};

export const stopAudio = () => {
  if (currentPlaying) {
    if (currentPlaying.playing()) {
      currentPlaying.stop();
    }
    currentPlaying = null;
  }
};

export const clearAllCache = async () => {
  try {
    const success = await caches.delete(CACHE_NAME);
    metadataStore.clear();
    audioCache = null;

    console.log("Cache completamente limpo:", success);
    return success;
  } catch (error) {
    console.error("Erro ao limpar cache completo:", error);
    LogRocket.log({ error: "clear_all_cache_error", details: error.message });
    return false;
  }
};
